import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { NavigationMenuLink } from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { IMenuItem } from '@/types/navigation';
import { ChevronRight } from 'lucide-react';
import Link from 'next/link';

type NestedHoverMenuProps = {
  menu: IMenuItem[];
  themeId?: string;
  token?: string;
  preview?: boolean;
  createUrl: (
    url_path: string,
    opts?: {
      themeId?: string;
      token?: string;
      preview?: boolean;
    }
  ) => string;
};

export function DropdownMenu({ menu, themeId, token, preview, createUrl }: NestedHoverMenuProps) {
  return (
    <div className='grid rounded-none p-2'>
      {/* Dropdown Menu */}
      {menu.map((subMenu, idx) => (
        <div key={idx} className='relative'>
          {subMenu.children.length > 0 ? (
            <HoverCard openDelay={100} closeDelay={100}>
              <HoverCardTrigger asChild>
                <div className='flex cursor-pointer items-center justify-between rounded p-2'>
                  <div className='flex items-center space-x-3'>
                    <NavigationMenuLink asChild>
                      <Link
                        href={createUrl(subMenu.url_path || '', {
                          themeId,
                          token,
                          preview,
                        })}
                        data-testid={`dropdown-menu-${subMenu.title}`}
                      >
                        <span className='menu-item-with-hover-border text-hover-border'>
                          {subMenu.title}
                        </span>
                      </Link>
                    </NavigationMenuLink>
                  </div>
                  <ChevronRight className='h-4 w-4' />
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                side='right'
                align='start'
                className='z-50 ml-3 max-w-[200px] rounded-none bg-[--menu-background] text-[--menu-text]'
              >
                <div className='grid gap-1'>
                  {subMenu.children.map((childMenu, childIdx) => (
                    <NestedHoverMenu
                      key={childIdx}
                      node={childMenu}
                      themeId={themeId}
                      token={token}
                      preview={preview}
                      createUrl={createUrl}
                    />
                  ))}
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <NavigationMenuLink asChild>
              <Link
                href={createUrl(subMenu.url_path || '', {
                  themeId,
                  token: token as string,
                  preview,
                })}
                className='group flex items-center justify-between rounded p-2 transition-colors'
              >
                <div className='flex items-center space-x-3'>
                  <span className='menu-item-with-hover-border text-hover-border'>
                    {subMenu.title}
                  </span>
                </div>
              </Link>
            </NavigationMenuLink>
          )}
        </div>
      ))}
    </div>
  );
}

type NestedHoverItemProps = {
  node: IMenuItem;
  themeId?: string;
  token?: string;
  preview?: boolean;
  createUrl: (
    url_path: string,
    opts?: {
      themeId?: string;
      token?: string;
      preview?: boolean;
    }
  ) => string;
};

function NestedHoverMenu({ node, themeId, token, preview, createUrl }: NestedHoverItemProps) {
  const hasChildren = !!node.children?.length;

  if (!hasChildren) {
    return (
      <NavigationMenuLink asChild>
        <Link
          href={createUrl(node.url_path || '', { themeId, token, preview })}
          className='block rounded p-2 transition-colors'
        >
          <span className='menu-item-with-hover-border text-hover-border'>{node.title}</span>
        </Link>
      </NavigationMenuLink>
    );
  }

  return (
    <HoverCard openDelay={100} closeDelay={150}>
      <HoverCardTrigger asChild>
        <div className='flex cursor-pointer items-center justify-between rounded'>
          <NavigationMenuLink asChild>
            <Link href={createUrl(node.url_path || '', { themeId, token, preview })}>
              <span className='menu-item-with-hover-border text-hover-border'>{node.title}</span>
            </Link>
          </NavigationMenuLink>
          <ChevronRight className='h-4 w-4' />
        </div>
      </HoverCardTrigger>
      <HoverCardContent
        side='right'
        align='start'
        sideOffset={10}
        className={cn(
          'z-50 ml-2 max-w-[200px] rounded-none p-2',
          'transition-opacity duration-150 data-[state=closed]:opacity-0 data-[state=open]:opacity-100',
          'pointer-events-none data-[state=open]:pointer-events-auto',
          'bg-[--menu-background] text-[--menu-text]'
        )}
      >
        <div className='grid gap-1'>
          {node.children!.map((child) => (
            <NestedHoverMenu
              key={child.id}
              node={child}
              themeId={themeId}
              token={token}
              preview={preview}
              createUrl={createUrl}
            />
          ))}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
